import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { MarketingBrand } from '../../types';

// ─── helpers ─────────────────────────────────────────────────────────────────

function arrToText(arr: unknown): string {
  if (!Array.isArray(arr)) return '';
  return arr.join('\n');
}
function textToArr(text: string): string[] {
  return text.split('\n').map(s => s.trim()).filter(Boolean);
}
function parseLinearGradient(value: string) {
  const fallback = { angle: '165', start: '#1E3A8A', middle: '#2563EB', end: '#60A5FA' };
  if (!value) return fallback;
  const angle = value.match(/linear-gradient\(\s*(\d+)deg/i)?.[1] || fallback.angle;
  const colors = value.match(/#[0-9a-fA-F]{6}/g) || [];
  return {
    angle,
    start: colors[0] || fallback.start,
    middle: colors[1] || fallback.middle,
    end: colors[2] || fallback.end,
  };
}

function buildLinearGradient(angle: string, start: string, middle: string, end: string) {
  return `linear-gradient(${angle || '165'}deg, ${start || '#1E3A8A'} 0%, ${middle || '#2563EB'} 50%, ${end || '#60A5FA'} 100%)`;
}

// ─── sub-components ───────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}
const inp = 'w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200';
const ta = `${inp} resize-y`;

// ─── main component ───────────────────────────────────────────────────────────

type Tab = 'essencia' | 'audiencia' | 'oferta' | 'voz' | 'visual' | 'restricoes';
const TABS: { id: Tab; label: string }[] = [
  { id: 'essencia',  label: 'Essencia'    },
  { id: 'audiencia', label: 'Audiencia'   },
  { id: 'oferta',    label: 'Oferta'      },
  { id: 'voz',       label: 'Voz'         },
  { id: 'visual',    label: 'Visual'      },
  { id: 'restricoes',label: 'Restricoes'  },
];

interface Props {
  brand: MarketingBrand;
  onSaved: (brand: MarketingBrand) => void;
  onClose: () => void;
}

export function BrandEditDrawer({ brand, onSaved, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('essencia');
  const [saving, setSaving] = useState(false);
  const [matrixAgentRunning, setMatrixAgentRunning] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // ── Essencia ──
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [handle, setHandle] = useState('');
  const [website, setWebsite] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [promise, setPromise] = useState('');
  const [personality, setPersonality] = useState('');

  // ── Audiencia ──
  const [audPrimary, setAudPrimary] = useState('');
  const [audDream, setAudDream] = useState('');
  const [audFears, setAudFears] = useState('');
  const [audPastFailures, setAudPastFailures] = useState('');
  const [audCentralSuspicion, setAudCentralSuspicion] = useState('');
  const [audEthicalEnemy, setAudEthicalEnemy] = useState('');

  // ── Oferta ──
  const [offerDreamOutcome, setOfferDreamOutcome] = useState('');
  const [offerTimeToResult, setOfferTimeToResult] = useState('');
  const [offerGuarantee, setOfferGuarantee] = useState('');
  const [offerWeakestLever, setOfferWeakestLever] = useState('');
  const [offerProblemsSolved, setOfferProblemsSolved] = useState('');

  // ── Voz ──
  const [voiceTone, setVoiceTone] = useState('');
  const [voiceSpeaks, setVoiceSpeaks] = useState('');
  const [voiceDoesNotSpeak, setVoiceDoesNotSpeak] = useState('');

  // ── Visual ──
  const [colorPrimary, setColorPrimary] = useState('');
  const [colorLight, setColorLight] = useState('');
  const [colorDark, setColorDark] = useState('');
  const [colorLightBg, setColorLightBg] = useState('');
  const [colorDarkBg, setColorDarkBg] = useState('');
  const [colorGradient, setColorGradient] = useState('');
  const [gradientAngle, setGradientAngle] = useState('165');
  const [gradientStart, setGradientStart] = useState('#1E3A8A');
  const [gradientMiddle, setGradientMiddle] = useState('#2563EB');
  const [gradientEnd, setGradientEnd] = useState('#60A5FA');
  const [fontDisplay, setFontDisplay] = useState('');
  const [fontBody, setFontBody] = useState('');
  const [fontUrl, setFontUrl] = useState('');
  const [designPrinciples, setDesignPrinciples] = useState('');
  const [vdSystem, setVdSystem] = useState('');
  const [vdRationale, setVdRationale] = useState('');

  // ── Restricoes ──
  const [rules, setRules] = useState('');

  // ── seed form from brand ──
  useEffect(() => {
    const aud = (brand.audience as any) || {};
    const offer = (brand.offer as any) || {};
    const voice = (brand.voice as any) || {};
    const vt = (brand.visualTokens as any) || {};
    const rest = (brand.restrictions as any) || {};
    const colors = vt.colors || {};
    const fonts = vt.fonts || {};
    const vd = vt.visualDirection || {};

    setName(brand.name || '');
    setCategory((brand as any).category || '');
    setHandle((brand as any).handle || '');
    setWebsite((brand as any).website || '');
    setLogoUrl((brand as any).logoUrl || '');
    setPromise((brand as any).promise || '');
    setPersonality(arrToText(brand.personality));

    setAudPrimary(aud.primary || '');
    setAudDream(aud.dream || '');
    setAudFears(aud.fears || '');
    setAudPastFailures(aud.pastFailures || '');
    setAudCentralSuspicion(aud.centralSuspicion || '');
    setAudEthicalEnemy(aud.ethicalEnemy || '');

    setOfferDreamOutcome(offer.dreamOutcome || '');
    setOfferTimeToResult(offer.timeToResult || '');
    setOfferGuarantee(offer.guarantee || '');
    setOfferWeakestLever(offer.weakestLever || '');
    setOfferProblemsSolved(arrToText(offer.problemsSolved));

    setVoiceTone(voice.tone || '');
    setVoiceSpeaks(arrToText(voice.speaks));
    setVoiceDoesNotSpeak(arrToText(voice.doesNotSpeak));

    setColorPrimary(colors.primary || '');
    setColorLight(colors.light || '');
    setColorDark(colors.dark || '');
    setColorLightBg(colors.lightBg || '');
    setColorDarkBg(colors.darkBg || '');
    const parsedGradient = parseLinearGradient(colors.gradient || '');
    setColorGradient(colors.gradient || buildLinearGradient(parsedGradient.angle, parsedGradient.start, parsedGradient.middle, parsedGradient.end));
    setGradientAngle(parsedGradient.angle);
    setGradientStart(parsedGradient.start);
    setGradientMiddle(parsedGradient.middle);
    setGradientEnd(parsedGradient.end);
    setFontDisplay(fonts.display || '');
    setFontBody(fonts.body || '');
    setFontUrl(fonts.url || '');
    setDesignPrinciples(arrToText(vt.designPrinciples));
    setVdSystem(vd.system || '');
    setVdRationale(vd.rationale || '');

    setRules(arrToText(rest.rules));
  }, [brand]);

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Nome obrigatorio');

    const { brandMarkdown: _removedBrandMarkdown, brandSpecMarkdown: _removedBrandSpecMarkdown, applications: _removedApplications, ...existingOffer } = ((brand.offer as any) || {});
    const existingVt = (brand.visualTokens as any) || {};
    const existingFonts = existingVt.fonts || {};
    const existingVd = existingVt.visualDirection || {};

    const payload = {
      name: name.trim(),
      category: category.trim() || undefined,
      handle: handle.trim() || undefined,
      website: website.trim() || undefined,
      logoUrl: logoUrl.trim() || undefined,
      promise: promise.trim() || undefined,
      personality: textToArr(personality),
      audience: {
        ...((brand.audience as any) || {}),
        primary: audPrimary,
        dream: audDream,
        fears: audFears,
        pastFailures: audPastFailures,
        centralSuspicion: audCentralSuspicion,
        ethicalEnemy: audEthicalEnemy,
      },
      offer: {
        ...existingOffer,
        dreamOutcome: offerDreamOutcome,
        timeToResult: offerTimeToResult,
        guarantee: offerGuarantee,
        weakestLever: offerWeakestLever,
        problemsSolved: textToArr(offerProblemsSolved),
      },
      voice: {
        ...((brand.voice as any) || {}),
        tone: voiceTone,
        speaks: textToArr(voiceSpeaks),
        doesNotSpeak: textToArr(voiceDoesNotSpeak),
      },
      visualTokens: {
        ...existingVt,
        colors: {
          primary: colorPrimary,
          // Todos os papéis semânticos são reconstruídos a partir dos campos
          // visíveis. Nunca reenviar accent/text/value ocultos de uma versão antiga.
          secondary: colorLight || colorPrimary,
          accent: colorPrimary,
          background: colorLightBg,
          text: colorDark,
          light: colorLight,
          dark: colorDark,
          lightBg: colorLightBg,
          lightBorder: colorLightBg,
          darkBg: colorDarkBg,
          gradient: colorGradient,
          alert: colorPrimary,
          value: colorPrimary,
        },
        colorSource: 'user_edit',
        fonts: {
          ...existingFonts,
          display: fontDisplay,
          body: fontBody,
          url: fontUrl,
        },
        designPrinciples: textToArr(designPrinciples),
        visualDirection: {
          ...existingVd,
          system: vdSystem,
          rationale: vdRationale,
        },
      },
      restrictions: {
        ...((brand.restrictions as any) || {}),
        rules: textToArr(rules),
      },
    };

    const matrixRelevant = (value: any) => {
      const relevantOffer = { ...(value.offer || {}) };
      for (const key of ['campaignMatrix', 'brandMarkdown', 'brandSpecMarkdown', 'applications', 'sourceAnswers', 'generatedBy']) delete relevantOffer[key];
      const relevantRestrictions = { ...(value.restrictions || {}) };
      delete relevantRestrictions.checklist;
      return {
        category: value.category || null,
        promise: value.promise || null,
        personality: value.personality || [],
        audience: value.audience || {},
        offer: relevantOffer,
        voice: value.voice || {},
        restrictions: relevantRestrictions,
      };
    };
    const strategicChange = JSON.stringify(matrixRelevant(brand)) !== JSON.stringify(matrixRelevant(payload));
    setSaving(true);
    setMatrixAgentRunning(strategicChange);
    try {
      const r = await api.updateMarketingBrand(brand.id, payload);
      toast.success('Brand atualizada');
      onSaved(r.brand);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar brand');
    } finally {
      setSaving(false);
      setMatrixAgentRunning(false);
    }
  };

  const updateGradientFromControls = (next: Partial<{ angle: string; start: string; middle: string; end: string }>) => {
    const angle = next.angle ?? gradientAngle;
    const start = next.start ?? gradientStart;
    const middle = next.middle ?? gradientMiddle;
    const end = next.end ?? gradientEnd;
    setGradientAngle(angle);
    setGradientStart(start);
    setGradientMiddle(middle);
    setGradientEnd(end);
    setColorGradient(buildLinearGradient(angle, start, middle, end));
  };

  const updateGradientCss = (value: string) => {
    setColorGradient(value);
    const parsed = parseLinearGradient(value);
    setGradientAngle(parsed.angle);
    setGradientStart(parsed.start);
    setGradientMiddle(parsed.middle);
    setGradientEnd(parsed.end);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/50">
      <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        {matrixAgentRunning && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/70 p-6 backdrop-blur-sm">
            <div className="max-w-sm rounded-3xl border border-indigo-300/30 bg-slate-950 p-7 text-center text-white shadow-2xl">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-300/30 border-t-indigo-300" />
              <p className="mt-5 text-lg font-black">Agente LLM ajustando sua matriz com os novos dados</p>
              <p className="mt-2 text-sm text-slate-300">A Brand só será salva depois que os ângulos estratégicos forem validados.</p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-950 px-6 py-4 text-white">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-300">Editar brand</p>
            <h2 className="mt-0.5 text-xl font-black">{brand.name}</h2>
          </div>
          <button onClick={onClose} className="rounded-xl bg-white/10 px-3 py-2 text-sm hover:bg-white/20">Fechar Fechar</button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-slate-200 bg-slate-50">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 px-4 py-3 text-sm font-semibold transition ${tab === t.id ? 'border-b-2 border-indigo-600 bg-white text-indigo-700' : 'text-slate-500 hover:text-slate-800'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'essencia' && (
            <div className="space-y-4">
              <Field label="Nome *">
                <input className={inp} value={name} onChange={e => setName(e.target.value)} />
              </Field>
              <Field label="Categoria">
                <input className={inp} value={category} onChange={e => setCategory(e.target.value)} placeholder="Ex: Moda, Saude, SaaS..." />
              </Field>
              <Field label="Promessa da marca">
                <textarea className={ta} rows={3} value={promise} onChange={e => setPromise(e.target.value)} />
              </Field>
              <Field label="@handle">
                <input className={inp} value={handle} onChange={e => setHandle(e.target.value)} placeholder="@suabrand" />
              </Field>
              <Field label="Website">
                <input className={inp} value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
              </Field>
              <Field label="Logo da marca">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo" className="h-14 w-14 rounded-xl border border-slate-200 object-contain bg-slate-50 shrink-0" onError={e => (e.currentTarget.style.display = 'none')} />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-2xl"></div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition ${uploadingLogo ? 'opacity-50 pointer-events-none' : ''}`}>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploadingLogo(true);
                          try {
                            const form = new FormData();
                            form.append('logo', file);
                            const r = await api.uploadBrandLogo(brand.id, form);
                            setLogoUrl(r.logoUrl);
                            toast.success('Logo enviada!');
                          } catch (err: any) {
                            toast.error(err.message || 'Erro ao enviar logo');
                          } finally {
                            setUploadingLogo(false);
                          }
                        }}
                      />
                      {uploadingLogo ? 'Enviando...' : logoUrl ? ' Trocar logo' : ' Enviar logo'}
                    </label>
                    {logoUrl && (
                      <button type="button" onClick={() => setLogoUrl('')} className="text-xs text-slate-400 hover:text-red-500 text-left transition">
                        Remover logo
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">PNG, JPG ou SVG ate 10 MB. Enviada ao agente de imagens para uso nos assets.</p>
              </Field>
              <Field label="Personalidade (um traco por linha)">
                <textarea className={ta} rows={4} value={personality} onChange={e => setPersonality(e.target.value)} placeholder={"Direto\nEmpatico\nArriscado"} />
              </Field>
            </div>
          )}

          {tab === 'audiencia' && (
            <div className="space-y-4">
              <Field label="Perfil primario">
                <textarea className={ta} rows={3} value={audPrimary} onChange={e => setAudPrimary(e.target.value)} />
              </Field>
              <Field label="Sonho / resultado desejado">
                <textarea className={ta} rows={3} value={audDream} onChange={e => setAudDream(e.target.value)} />
              </Field>
              <Field label="Medos">
                <textarea className={ta} rows={3} value={audFears} onChange={e => setAudFears(e.target.value)} />
              </Field>
              <Field label="Falhas anteriores">
                <textarea className={ta} rows={3} value={audPastFailures} onChange={e => setAudPastFailures(e.target.value)} />
              </Field>
              <Field label="Suspeita central">
                <textarea className={ta} rows={2} value={audCentralSuspicion} onChange={e => setAudCentralSuspicion(e.target.value)} />
              </Field>
              <Field label="Inimigo etico">
                <input className={inp} value={audEthicalEnemy} onChange={e => setAudEthicalEnemy(e.target.value)} />
              </Field>
            </div>
          )}

          {tab === 'oferta' && (
            <div className="space-y-4">
              <Field label="Resultado sonhado">
                <textarea className={ta} rows={3} value={offerDreamOutcome} onChange={e => setOfferDreamOutcome(e.target.value)} />
              </Field>
              <Field label="Prazo para resultado">
                <input className={inp} value={offerTimeToResult} onChange={e => setOfferTimeToResult(e.target.value)} placeholder="Ex: 30 dias, 3 meses..." />
              </Field>
              <Field label="Garantia">
                <input className={inp} value={offerGuarantee} onChange={e => setOfferGuarantee(e.target.value)} />
              </Field>
              <Field label="Alavanca mais fraca do cliente">
                <input className={inp} value={offerWeakestLever} onChange={e => setOfferWeakestLever(e.target.value)} />
              </Field>
              <Field label="Problemas resolvidos (um por linha)">
                <textarea className={ta} rows={4} value={offerProblemsSolved} onChange={e => setOfferProblemsSolved(e.target.value)} />
              </Field>
            </div>
          )}

          {tab === 'voz' && (
            <div className="space-y-4">
              <Field label="Tom de voz">
                <input className={inp} value={voiceTone} onChange={e => setVoiceTone(e.target.value)} placeholder="Ex: Direto, especialista, sem rodeios..." />
              </Field>
              <Field label="Fala (um por linha - o que a marca faz)">
                <textarea className={ta} rows={6} value={voiceSpeaks} onChange={e => setVoiceSpeaks(e.target.value)} />
              </Field>
              <Field label="Nao fala (um por linha - o que evita)">
                <textarea className={ta} rows={6} value={voiceDoesNotSpeak} onChange={e => setVoiceDoesNotSpeak(e.target.value)} />
              </Field>
            </div>
          )}

          {tab === 'visual' && (
            <div className="space-y-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Cores (HEX)</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Primary">
                  <div className="flex gap-2">
                    <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={colorPrimary || '#2563EB'} onChange={e => setColorPrimary(e.target.value)} />
                    <input className={inp} value={colorPrimary} onChange={e => setColorPrimary(e.target.value)} placeholder="#2563EB" />
                  </div>
                </Field>
                <Field label="Light">
                  <div className="flex gap-2">
                    <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={colorLight || '#60A5FA'} onChange={e => setColorLight(e.target.value)} />
                    <input className={inp} value={colorLight} onChange={e => setColorLight(e.target.value)} placeholder="#60A5FA" />
                  </div>
                </Field>
                <Field label="Dark">
                  <div className="flex gap-2">
                    <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={colorDark || '#1E3A8A'} onChange={e => setColorDark(e.target.value)} />
                    <input className={inp} value={colorDark} onChange={e => setColorDark(e.target.value)} placeholder="#1E3A8A" />
                  </div>
                </Field>
                <Field label="Light BG">
                  <div className="flex gap-2">
                    <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={colorLightBg || '#F8FAFC'} onChange={e => setColorLightBg(e.target.value)} />
                    <input className={inp} value={colorLightBg} onChange={e => setColorLightBg(e.target.value)} placeholder="#F8FAFC" />
                  </div>
                </Field>
                <Field label="Dark BG">
                  <div className="flex gap-2">
                    <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={colorDarkBg || '#0F172A'} onChange={e => setColorDarkBg(e.target.value)} />
                    <input className={inp} value={colorDarkBg} onChange={e => setColorDarkBg(e.target.value)} placeholder="#0F172A" />
                  </div>
                </Field>
              </div>
              <Field label="Gradient (CSS)">
                <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Angulo</span>
                      <input
                        type="number"
                        min="0"
                        max="360"
                        className={inp}
                        value={gradientAngle}
                        onChange={e => updateGradientFromControls({ angle: e.target.value })}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Inicio</span>
                      <div className="flex gap-2">
                        <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={gradientStart} onChange={e => updateGradientFromControls({ start: e.target.value })} />
                        <input className={inp} value={gradientStart} onChange={e => updateGradientFromControls({ start: e.target.value })} />
                      </div>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Meio</span>
                      <div className="flex gap-2">
                        <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={gradientMiddle} onChange={e => updateGradientFromControls({ middle: e.target.value })} />
                        <input className={inp} value={gradientMiddle} onChange={e => updateGradientFromControls({ middle: e.target.value })} />
                      </div>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Fim</span>
                      <div className="flex gap-2">
                        <input type="color" className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300" value={gradientEnd} onChange={e => updateGradientFromControls({ end: e.target.value })} />
                        <input className={inp} value={gradientEnd} onChange={e => updateGradientFromControls({ end: e.target.value })} />
                      </div>
                    </label>
                  </div>
                  <input className={`${inp} font-mono text-xs`} value={colorGradient} onChange={e => updateGradientCss(e.target.value)} placeholder="linear-gradient(165deg, #1E3A8A 0%, #2563EB 50%, #60A5FA 100%)" />
                  {colorGradient && <div className="h-10 rounded-lg border border-slate-200" style={{ background: colorGradient }} />}
                </div>
              </Field>

              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Tipografia</p>
              <Field label="Fonte Display (headlines)">
                <input className={inp} value={fontDisplay} onChange={e => setFontDisplay(e.target.value)} placeholder="Plus Jakarta Sans" />
              </Field>
              <Field label="Fonte Body (texto corrido)">
                <input className={inp} value={fontBody} onChange={e => setFontBody(e.target.value)} placeholder="Inter" />
              </Field>
              <Field label="URL Google Fonts">
                <input className={inp} value={fontUrl} onChange={e => setFontUrl(e.target.value)} placeholder="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans..." />
              </Field>

              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Direcao visual</p>
              <Field label="Sistema visual">
                <input className={inp} value={vdSystem} onChange={e => setVdSystem(e.target.value)} placeholder="Ex: Clean Premium com espacamento generoso..." />
              </Field>
              <Field label="Racional">
                <textarea className={ta} rows={3} value={vdRationale} onChange={e => setVdRationale(e.target.value)} />
              </Field>
              <Field label="Principios de design (um por linha)">
                <textarea className={ta} rows={5} value={designPrinciples} onChange={e => setDesignPrinciples(e.target.value)} />
              </Field>
            </div>
          )}

          {tab === 'restricoes' && (
            <div className="space-y-4">
              <Field label="Regras (uma por linha)">
                <textarea className={ta} rows={8} value={rules} onChange={e => setRules(e.target.value)} placeholder={"Nunca use linguagem infantil\nSempre use dados reais..."} />
              </Field>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`h-2 w-2 rounded-full transition ${tab === t.id ? 'bg-indigo-600' : 'bg-slate-300'}`}
                title={t.label}
                aria-label={`Ir para ${t.label}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">Cancelar</button>
            <button onClick={handleSave} disabled={saving || !name.trim()} className="rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50">
              {matrixAgentRunning ? 'Agente LLM ajustando sua matriz...' : saving ? 'Salvando...' : 'Salvar tudo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
